import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { getAllCarModels } from '../../services/carModelsService';
import { preloadCarAssets } from '../../utils/imagePreloader';
import { trackFunnelStep, FUNNEL_STEPS } from '../../utils/analytics';
import { Search, Loader2, AlertCircle } from 'lucide-react';
import debounce from 'lodash/debounce';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';

const CarModelSelector = ({ onSelectModel }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [carModels, setCarModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch car models from Supabase
  useEffect(() => {
    const fetchModels = async () => {
      try {
        setLoading(true);
        const models = await getAllCarModels();
        setCarModels(models);
        setError(null);
      } catch (err) {
        console.error('Error fetching car models:', err);
        setError('Failed to load car models');
        setCarModels([]);
      } finally {
        setLoading(false);
      }
    };

    fetchModels();
  }, []);

  const filteredCars = useMemo(() => {
    const query = searchQuery.toLowerCase();
    return carModels.filter(car => 
      car.name.toLowerCase().includes(query) || 
      car.brand.toLowerCase().includes(query) ||
      car.year.toString().includes(query)
    );
  }, [searchQuery, carModels]);

  // Debounced function to track search
  const trackSearch = useCallback(
    debounce((query, resultsCount) => {
      // Only track if query is not empty
      if (query.trim()) {
        // Track in Google Analytics
        window.gtag?.('event', 'car_search', {
          event_category: 'Car Customizer',
          event_label: query,
          search_term: query,
          results_count: resultsCount
        });

        // Track in our funnel tracking
        trackFunnelStep(FUNNEL_STEPS.CAR_SEARCH, {
          search_query: query,
          results_count: resultsCount
        });
      }
    }, 1000), // Wait 1 second after user stops typing
    []
  );

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    trackSearch(query, filteredCars.length);
  };

  const handleMouseEnter = (car) => {
    // Preload assets when hovering over a car model
    // Convert carModels array to object format for backward compatibility
    const modelsObject = {};
    carModels.forEach(model => {
      modelsObject[model.id] = model;
    });
    preloadCarAssets(car.id, modelsObject);
  };

  const handleCarSelect = (car) => {
    trackFunnelStep(FUNNEL_STEPS.CAR_SELECTED, {
      car_model: car.name
    });
    onSelectModel(car);
  };

  if (loading) {
    return (
      <div className="container py-12">
        <h2 className="text-4xl font-bold text-center mb-8">Börja med att välja din bilmodell</h2>
        <div className="flex justify-center items-center gap-3">
          <Loader2 className="h-6 w-6 animate-spin" />
          <span>Laddar bilmodeller...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container py-12">
        <h2 className="text-4xl font-bold text-center mb-8">Börja med att välja din bilmodell</h2>
        <Alert variant="destructive" className="max-w-md mx-auto">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Fel</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
        <div className="flex justify-center mt-6">
          <Button onClick={() => window.location.reload()}>
            Försök igen
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary/50">
      <div className="container py-12">
      <h2 className="text-4xl font-bold text-center mb-8">Börja med att välja din bilmodell</h2>
      
      <div className="flex justify-between items-center mb-6">
        <Badge variant="secondary">
          {filteredCars.length} {filteredCars.length === 1 ? 'bil' : 'bilar'} tillgängliga
        </Badge>
        <div className="relative w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Sök efter bilmodell..."
            value={searchQuery}
            onChange={handleSearchChange}
            className="pl-10"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredCars.length > 0 ? (
          filteredCars.map((car) => (
            <Card
              key={car.id}
              className="group cursor-pointer hover:scale-105 hover:shadow-lg transition-all overflow-hidden"
              onClick={() => handleCarSelect(car)}
              onMouseEnter={() => handleMouseEnter(car)}
            >
              <CardContent className="p-0">
                <div className="relative">
                  <img
                    src={car.gridPicture || car.previewImage || '/images/placeholder-car.png'}
                    alt={car.name}
                    className="w-full h-40 object-contain"
                    onError={(e) => {
                      console.warn('Grid picture failed to load:', e.target.src);
                      e.target.src = '/images/placeholder-car.png';
                    }}
                  />
                  <div className="absolute inset-0 bg-background/95 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button size="sm">Börja designa</Button>
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="font-semibold text-lg">{car.name}</h3>
                  <p className="text-sm text-muted-foreground">{car.brand} {car.year}</p>
                </div>
              </CardContent>
            </Card>
          ))
        ) : (
          <div className="col-span-full text-center py-12">
            <p className="text-muted-foreground mb-4">Inga bilar matchade din sökning</p>
            <Button variant="link" onClick={() => setSearchQuery('')}>
              Visa alla bilar
            </Button>
          </div>
        )}
      </div>

      {/* Contact section for missing car models */}
      <Card className="mt-8 max-w-2xl mx-auto">
        <CardHeader>
          <CardTitle className="text-center">Hittade du inte din bilmodell?</CardTitle>
        </CardHeader>
        <CardContent className="text-center">
          <p className="text-muted-foreground mb-4">
            Vi lägger till nya bilmodeller regelbundet. Låt oss veta vilken bil du söker!
          </p>
          <Button asChild>
            <a href="mailto:info@dekalio.nu">Kontakta oss</a>
          </Button>
        </CardContent>
      </Card>
      </div>
    </div>
  );
};

export default CarModelSelector;